import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

const VideoSection = ({ isTamil }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef(null);

  const translations = {
    title: {
      en: "Watch Our Journey",
      ta: "எங்கள் பயணத்தைப் பாருங்கள்"
    },
    subtitle: {
      en: "Discover the transformative experiences of Scouts and Guides in Tamil Nadu",
      ta: "தமிழ்நாட்டில் சாரணர்கள் மற்றும் வழிகாட்டிகளின் மாற்றமளிக்கும் அனுபவங்களைக் கண்டறியுங்கள்"
    },
    watchVideo: {
      en: "Watch Video",
      ta: "காணொளியைப் பார்க்க"
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-[#f5f3ef] to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className={`text-[#2C251D] mb-4 ${
            isTamil 
              ? 'text-3xl md:text-4xl'
              : 'text-4xl md:text-5xl'
          }`}>
            {translations.title[isTamil ? 'ta' : 'en']}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {translations.subtitle[isTamil ? 'ta' : 'en']}
          </p>
        </motion.div>

        {/* Video Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto"
        >
          <div 
            className="relative rounded-2xl overflow-hidden shadow-2xl bg-black group"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            {/* Video Element */}
            <video
              ref={videoRef}
              className="w-full aspect-video object-cover"
              src="/videos/scout-journey.mp4"
              poster="/Images/video-thumbnail.jpg"
              onEnded={() => setIsPlaying(false)}
              onClick={togglePlay}
            >
              Your browser does not support the video tag.
            </video>

            {/* Play Overlay - Shows when video is paused */}
            {!isPlaying && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                onClick={togglePlay}
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-20 h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center
                           shadow-2xl transition-all duration-300"
                >
                  <Play className="w-10 h-10 text-[#8A7968] ml-1" fill="currentColor" />
                </motion.button>
              </motion.div>
            )}

            {/* Video Controls - Shows on hover */}
            {showControls && isPlaying && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6"
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Play/Pause Button */}
                  <button
                    onClick={togglePlay}
                    className="text-white hover:text-[#e7dd7a] transition-colors duration-200"
                  >
                    {isPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6" fill="currentColor" />
                    )}
                  </button>

                  {/* Right Side Controls */}
                  <div className="flex items-center gap-4">
                    {/* Mute/Unmute Button */}
                    <button
                      onClick={toggleMute}
                      className="text-white hover:text-[#e7dd7a] transition-colors duration-200"
                    >
                      {isMuted ? (
                        <VolumeX className="w-6 h-6" />
                      ) : (
                        <Volume2 className="w-6 h-6" />
                      )}
                    </button>

                    {/* Fullscreen Button */}
                    <button
                      onClick={toggleFullscreen}
                      className="text-white hover:text-[#e7dd7a] transition-colors duration-200"
                    >
                      <Maximize className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Video Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 text-center"
          >
            <p className="text-gray-600 text-base max-w-3xl mx-auto leading-relaxed">
              {isTamil 
                ? "சாரணர் மற்றும் வழிகாட்டி இயக்கம் இளைஞர்களுக்கு தலைமைத்துவம், குழுப்பணி மற்றும் சமூக பொறுப்புணர்வை வளர்க்கிறது."
                : "The Scout and Guide movement develops leadership, teamwork, and social responsibility among young people through adventurous activities and community service."}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;